import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Microphone, X, PaperPlaneTilt, Stop } from "@phosphor-icons/react";

/**
 * Records a voice note via MediaRecorder, shows a live duration counter
 * and a simple bar visualiser, uploads on Send. Calls onSent with the
 * uploaded attachment metadata so the parent can attach it to a message.
 */
export default function VoiceRecorder({ onCancel, onSent }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickRef = useRef(null);
  const [level, setLevel] = useState(0); // 0-1 mic loudness for the bar
  const analyserRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => () => stopAll(), []);

  const stopAll = () => {
    try { recorderRef.current?.stop(); } catch (e) { /* already stopped */ }
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch (e) { /* already closed */ }
    try { audioCtxRef.current?.close(); } catch (e) { /* already closed */ }
    if (tickRef.current) clearInterval(tickRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  };

  const start = async () => {
    setErr(""); setSeconds(0); chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream);
      recorderRef.current = rec;
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.start();
      // live meter
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const an = ctx.createAnalyser();
      an.fftSize = 256;
      src.connect(an);
      analyserRef.current = an;
      const buf = new Uint8Array(an.frequencyBinCount);
      const tickMeter = () => {
        an.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        setLevel(Math.min(1, Math.sqrt(sum / buf.length) * 3));
        rafRef.current = requestAnimationFrame(tickMeter);
      };
      tickMeter();
      tickRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
      setRecording(true);
    } catch (e) {
      setErr("Microphone access denied. Allow it in your browser settings.");
    }
  };

  const stop = async () => {
    if (!recorderRef.current || recorderRef.current.state === "inactive") return null;
    return new Promise(resolve => {
      recorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
        resolve(blob);
      };
      recorderRef.current.stop();
      streamRef.current?.getTracks().forEach(t => t.stop());
      if (tickRef.current) clearInterval(tickRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setRecording(false);
    });
  };

  const sendNote = async () => {
    setBusy(true);
    try {
      const blob = await stop();
      if (!blob || blob.size === 0) { setErr("No audio captured"); setBusy(false); return; }
      const ext = blob.type.includes("ogg") ? "ogg" : (blob.type.includes("mp4") ? "m4a" : "webm");
      const fd = new FormData();
      fd.append("file", blob, `voice-${Date.now()}.${ext}`);
      const { data } = await api.post("/team-chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onSent?.({ ...data, kind: "audio", duration_sec: seconds });
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  useEffect(() => { start(); }, []);

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <div className="border border-[#5B5FC7] rounded-md bg-[#EBEBFC] p-2 flex items-center gap-3" data-testid="voice-recorder">
      <div className={`w-3 h-3 rounded-full ${recording ? "bg-[var(--danger)] animate-pulse" : "bg-[var(--text-muted)]"}`} />
      <Microphone size={16} className="text-[#5B5FC7]" weight="fill" />
      <span className="font-mono text-[13px] text-[#5B5FC7] font-semibold" data-testid="voice-timer">{mm}:{ss}</span>
      {/* Simple loudness bar */}
      <div className="flex-1 h-4 bg-white rounded relative overflow-hidden">
        <div className="absolute inset-y-0 left-0 bg-[#5B5FC7] transition-all"
             style={{ width: `${level * 100}%` }} />
      </div>
      {err && <span className="text-[11px] text-[var(--danger)]" data-testid="voice-error">{err}</span>}
      <button onClick={() => { stopAll(); onCancel?.(); }} className="p-1.5 hover:bg-white rounded text-[var(--text-secondary)]" title="Cancel"
              data-testid="voice-cancel">
        <X size={14} />
      </button>
      <button onClick={sendNote} disabled={busy || seconds < 1}
              className="p-1.5 bg-[#5B5FC7] text-white rounded hover:opacity-90 disabled:opacity-50" title="Send voice note"
              data-testid="voice-send">
        {busy ? <Stop size={14} weight="fill" /> : <PaperPlaneTilt size={14} weight="fill" />}
      </button>
    </div>
  );
}
